import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../lib/api";
import Reveal from "../components/Reveal";
import { PillButton, Overline } from "../components/primitives";

const HERO_IMG = "https://images.unsplash.com/photo-1691349810645-b2e04c1c82ee?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function WhyElSalvador() {
  const [data, setData] = useState({});
  useEffect(() => { api.get("/content/why_el_salvador").then((r) => setData(r.data)).catch(() => {}); }, []);

  const intro = (data.intro || "").split("\n\n").filter(Boolean);
  const bullets = data.bullets || [];
  const callout = (data.callout || "").split("\n").filter(Boolean);
  const sections = data.sections || [];

  return (
    <div className="pt-16 md:pt-20">
      {/* Hero */}
      <section className="relative h-[60vh] overflow-hidden">
        <img src={sections[1]?.image || HERO_IMG} alt="El Salvador" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 h-full flex items-end max-w-7xl mx-auto px-6 md:px-10 pb-12 text-white">
          <Reveal>
            <Overline className="text-white/80">The Destination</Overline>
            <h1 className="mt-3 font-display font-extrabold tracking-tighter text-4xl md:text-6xl">{data.heading || "Why El Salvador?"}</h1>
            <p className="mt-3 text-lg md:text-xl text-white/85 font-display">{data.subheading || "Adventure. Beauty. Culture. Energy."}</p>
          </Reveal>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10 space-y-5">
          {intro.map((p, i) => (
            <Reveal key={i} delay={i * 0.05}><p className="text-lg text-foreground/75 leading-relaxed">{p}</p></Reveal>
          ))}
        </div>
      </section>

      {/* Bullets */}
      {bullets.length > 0 && (
        <section className="pb-8">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <Reveal><h2 className="font-display font-extrabold tracking-tight text-3xl md:text-4xl">{data.bullets_heading || "Why You'll Want to Experience El Salvador"}</h2></Reveal>
            <div className="mt-8 grid md:grid-cols-2 gap-4">
              {bullets.map((b, i) => (
                <Reveal key={i} delay={(i % 2) * 0.08}>
                  <div className="h-full rounded-2xl border border-black/10 bg-white p-6 hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow] duration-200 text-foreground/80 leading-relaxed">
                    {b}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Existing imagery gallery */}
      {sections.length > 0 && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sections.map((s, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <div className="group relative rounded-3xl overflow-hidden h-64">
                  <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
                  <div className="absolute bottom-0 p-6 text-white">
                    <div className="font-display font-bold text-xl">{s.title}</div>
                    {s.body && <p className="mt-1 text-white/80 text-sm">{s.body}</p>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Final callout */}
      <section className="relative py-24 md:py-28 mt-8 bg-[#0D1110] text-white overflow-hidden">
        <div className="noise-overlay" />
        <img src={sections[0]?.image || HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            {callout.map((line, i) => (
              <p key={i} className={i === 0 ? "font-display font-extrabold tracking-tight text-3xl md:text-5xl leading-[1.05]" : "mt-4 text-lg md:text-xl text-primary font-display"}>{line}</p>
            ))}
            <div className="mt-9"><Link to="/book"><PillButton>Book Your Retreat <ArrowRight className="w-4 h-4" /></PillButton></Link></div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
